"use client";

// Next.js imports

// Sanity.io imports
import { PortableText } from "next-sanity";

// Component & Template imports
import NextLink from "@/components/interactive/NextLink";

// Hooks, Utility & Helper function imports
import { createPortableTextComponents } from "@/utils/portableTextComponents";
import NextImage from "@/components/interactive/NextImage";
import { t } from "@/utils/t";
// import imageSourceUrl from "@/utils/imageUrl";

import {
  IsovTabSwitcher,
  IsovAgendaItem,
  IsovAvatar,
  IsovButton,
  IsovTag,
} from "@/components/stencil";

import type { Dictionary } from "@/dictionaries";

interface AgendaProps {
  theme?: string;
  eyebrow?: any;
  headline?: any;
  description?: any;
  anchorId?: string;
  activeIndex?: number;
  decorated?: boolean;
  shaded?: boolean;
  agendaDayReferences?: any;
  locale?: string;
  skeleton?: boolean;
  dict?: Dictionary;
}

export default function Agenda({
  theme,
  eyebrow,
  headline,
  description,
  anchorId,
  activeIndex = 1,
  decorated,
  shaded,
  agendaDayReferences,
  locale = "en",
  skeleton,
  dict,
}: AgendaProps) {
  const ptComponents = createPortableTextComponents(locale);
  const agendaDays = agendaDayReferences
    ?.map((agendaDay: any) => t(agendaDay?.label, locale))
    ?.join(";");

  return (
    <IsovTabSwitcher
      theme={theme}
      eyebrow={t(eyebrow, locale)}
      headline={t(headline, locale)}
      subtext={t(description, locale)}
      labels={agendaDays}
      active={activeIndex}
      decorated={decorated}
      shaded={shaded}
      skeleton={skeleton}
    >
      {anchorId && <a id={anchorId} className="anchor"></a>}
      {agendaDayReferences?.map((agendaDay: any) => (
        <div key={agendaDay?._id} className="tab">
          {agendaDay?.agendaItemReferences?.map((agendaItem: any) => (
            <IsovAgendaItem
              key={agendaItem?._id}
              headline={t(agendaItem?.headline, locale)}
              // subtext={agendaItem?.authors
              //   ?.map(
              //     (author: any) =>
              //       `${t(author?.firstName, locale)} ${t(author?.lastName, locale)}`
              //   )
              //   ?.join(", ")}
              time={t(agendaItem?.time, locale)}
              location={t(agendaItem?.location, locale)}
              decorated={decorated}
              shaded={shaded}
              skeleton={skeleton}
              theme={theme}
              // eyebrow={agendaDay?.label?.[locale]}
            >
              <div
                slot="author"
                className="flex items-center justify-start mt-4 gap-2"
              >
                {agendaItem?.authors?.map((author: any) => (
                  <IsovAvatar
                    key={author?._id}
                    theme={theme}
                    size="medium"
                    name={`${t(author?.firstName, locale)} ${t(author?.lastName, locale)}`}
                    className="mr-1"
                  >
                    <NextImage
                      slot="avatar"
                      src={
                        author?.headshot
                          ? author?.image
                          : `/images/headshot.png`
                      }
                      alt={`${t(author?.firstName, locale)} ${t(author?.lastName, locale)} Image`}
                      maxWidth={200}
                      height={200}
                      width={200}
                      isOptimized
                    />
                  </IsovAvatar>
                ))}
              </div>

              {agendaItem?.description && (
                <>
                  {typeof agendaItem?.description === "string" ? (
                    agendaItem?.description
                  ) : (
                    <PortableText
                      value={t(agendaItem?.description, locale)}
                      components={ptComponents}
                      onMissingComponent={(message, options) => {
                        console.warn(message, {
                          type: options.type,
                          nodeType: options.nodeType,
                        });
                      }}
                    />
                  )}
                </>
              )}

              {agendaItem?.videoUrl && (
                <NextLink
                  href={agendaItem?.videoUrl}
                  type="button_default"
                  slot="cta"
                >
                  <IsovButton
                    theme={theme}
                    label={dict?.ui?.agenda?.watchNow ?? "Watch now"}
                    skeleton={skeleton}
                  />
                </NextLink>
              )}

              {t(agendaItem?.tags, locale) && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {t(agendaItem?.tags, locale)
                    .split(",")
                    .map((tag: string) => tag.trim())
                    .filter(Boolean)
                    .map((tag: string) => (
                      <IsovTag
                        key={tag}
                        theme={theme}
                        label={tag}
                        slim
                        interactive={false}
                      ></IsovTag>
                    ))}
                </div>
              )}

              {agendaItem?.ctas?.map((cta: any) => (
                <NextLink
                  key={`cta-${cta?._key}-${t(cta?.label, locale)}`}
                  href={cta?.url}
                  target={cta?.openInNewTab ? "_blank" : undefined}
                  type={`button_${cta?.variant ?? "default"}`}
                  slot="cta"
                >
                  <IsovButton
                    theme={theme}
                    variant={cta?.variant}
                    label={t(cta?.label, locale)}
                    skeleton={skeleton}
                  />
                </NextLink>
              ))}
            </IsovAgendaItem>
          ))}
        </div>
      ))}
    </IsovTabSwitcher>
  );
}
